import { getTranslateData } from "@/utils/translator.js";

/**
 * HotspotManager handles all hotspot-related operations including creation,
 * event handling, and visibility management
 */
export class HotspotManager {
  constructor() {
    this.hotspots = new Map(); // Store hotspot elements by ID
    this.activeHotspots = new Set(); // Track currently active hotspots
  }

  /**
   * Create desktop hotspot element
   * @param {Object} hotspotConfig - Hotspot configuration
   * @param {Function} clickCallback - Callback function for hotspot clicks
   * @returns {HTMLElement} Hotspot DOM element
   */
  createHotspot(hotspotConfig, clickCallback = null) {
    const hotspot = document.createElement("div");
    hotspot.className = "hotspot";
    hotspot.setAttribute("data-hotspot-id", hotspotConfig.id);
    hotspot.style.left = hotspotConfig.position.left;
    hotspot.style.top = hotspotConfig.position.top;

    // Create the orange circle container
    const circle = document.createElement("div");
    circle.className = "hotspot-circle";

    // Add hand icon using a simple and clean pointing finger SVG
    const icon = document.createElement("div");
    icon.className = "hotspot-icon";
    icon.innerHTML = `
      <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 0C7.3425 0 6 1.39844 6 3.125V18.75V19.2969V25C6 25.6875 5.46 26.25 4.8 26.25C4.14 26.25 3.6 25.6875 3.6 25V20.6484C3.45 20.7578 3.3075 20.8828 3.165 21L1.725 22.25C0.63 23.2031 0 24.6094 0 26.0938V29.0625C0 32.0312 1.2675 34.8438 3.4575 36.7422L3.8625 37.0938C6.0225 38.9688 8.7375 39.9922 11.5425 39.9922L20.4 40C25.7025 40 30 35.5234 30 30V25V22.5C30 20.4297 28.3875 18.75 26.4 18.75C25.47 18.75 24.63 19.1172 23.9925 19.7109C23.85 17.7734 22.2975 16.25 20.4 16.25C19.47 16.25 18.63 16.6172 17.9925 17.2109C17.85 15.2734 16.2975 13.75 14.4 13.75C13.4775 13.75 12.6375 14.1094 12 14.7031V3.125C12 1.39844 10.6575 0 9 0Z" fill="white"/>
      </svg>`;
    circle.appendChild(icon);

    // Add "Click" text inside the circle
    const clickText = document.createElement("div");
    clickText.className = "click-text";
    clickText.textContent = "Click";
    circle.appendChild(clickText);

    hotspot.appendChild(circle);

    // Add text label if provided
    if (hotspotConfig.label) {
      const label = document.createElement("div");
      label.className = "hotspot-label";
      label.textContent = getTranslateData(hotspotConfig.label);
      hotspot.appendChild(label);
    }

    // Add click handler if provided
    if (clickCallback) {
      hotspot.addEventListener("click", (e) => {
        e.stopPropagation();
        clickCallback(hotspot);
      });
    }

    // Store hotspot reference
    this.hotspots.set(hotspotConfig.id, {
      element: hotspot,
      config: hotspotConfig,
    });

    return hotspot;
  }

  /**
   * Create mobile hotspots container
   * @param {Array} hotspotsConfig - Array of hotspot configurations
   * @param {Object} headerConfig - Header configuration
   * @param {Function} clickCallback - Callback function for hotspot clicks
   * @returns {HTMLElement} Mobile hotspots container
   */
  createMobileHotspots(hotspotsConfig, headerConfig, clickCallback = null) {
    const container = document.createElement("div");
    container.className = "mobile-hotspots";

    // Add header if provided
    if (headerConfig) {
      const header = document.createElement("div");
      header.className = "mobile-hotspots-header";

      const title = document.createElement("h3");
      title.className = "mobile-hotspots-title";
      title.textContent =
        getTranslateData(headerConfig.title) || headerConfig.title;
      header.appendChild(title);

      if (headerConfig.subtitle) {
        const subtitle = document.createElement("p");
        subtitle.className = "mobile-hotspots-subtitle";
        subtitle.textContent =
          getTranslateData(headerConfig.subtitle) || headerConfig.subtitle;
        header.appendChild(subtitle);
      }

      container.appendChild(header);
    }

    // Create buttons container
    const buttonsContainer = document.createElement("div");
    buttonsContainer.className = "mobile-hotspots-buttons";

    hotspotsConfig.forEach((hotspotConfig) => {
      const button = document.createElement("button");
      button.className = "mobile-hotspot-button";
      button.setAttribute("data-hotspot-id", hotspotConfig.id);
      button.textContent =
        getTranslateData(hotspotConfig.label) || `Hotspot ${hotspotConfig.id}`;

      // Add click handler if provided
      if (clickCallback) {
        button.addEventListener("click", (e) => {
          e.stopPropagation();
          // Create a fake hotspot element for compatibility
          const fakeHotspot = document.createElement("div");
          fakeHotspot.setAttribute("data-hotspot-id", hotspotConfig.id);
          fakeHotspot.className = "hotspot mobile-hotspot-proxy";
          clickCallback(fakeHotspot);
        });
      }

      buttonsContainer.appendChild(button);
    });

    container.appendChild(buttonsContainer);
    return container;
  }

  /**
   * Handle hotspot click event
   * @param {HTMLElement} hotspotElement - Clicked hotspot element
   * @param {Function} navigationCallback - Callback for navigation
   * @param {Object} currentStepConfig - Current step configuration
   */
  handleHotspotClick(hotspotElement, navigationCallback, currentStepConfig) {
    const hotspotId = hotspotElement.getAttribute("data-hotspot-id");
    console.log(`[Hotspot] ${hotspotId} clicked`);

    // Find hotspot config - use string ID directly, not parseInt
    const hotspotData = this.hotspots.get(hotspotId);
    if (!hotspotData) {
      console.warn(`!! [Hotspot] config not found for ID: ${hotspotId}`);
      return;
    }

    const hotspotConfig = hotspotData.config;

    // Add click animation
    this.addClickAnimation(hotspotElement);

    if (hotspotConfig.target) {
      // Navigate to the specific step defined in hotspot's target
      console.log(
        `[Hotspot] navigating to target step: ${hotspotConfig.target}`,
      );
      setTimeout(() => {
        navigationCallback(hotspotConfig.target);
      }, 500);
      return; // Prevent fallback to step's general transition
    }
    
    // Fall back to step's general transition logic only if no individual target
    if (currentStepConfig.transitions?.trigger === "hotspot-click") {
      if (currentStepConfig.transitions.next) {
        console.log(
          `[Hotspot] using step transition: ${currentStepConfig.transitions.next}`,
        );
        setTimeout(() => {
          navigationCallback(currentStepConfig.transitions.next);
        }, 500);
      }
    }
  }

  /**
   * Add click animation to hotspot
   * @param {HTMLElement} hotspotElement - Hotspot element to animate
   */
  addClickAnimation(hotspotElement) {
    const circle = hotspotElement.querySelector(".hotspot-circle");
    if (circle) {
      circle.style.transform = "scale(0.9)";
      setTimeout(() => {
        circle.style.transform = "";
      }, 150);
    }
  }

  /**
   * Update hotspot visibility based on active tab
   * @param {HTMLElement} stepElement - Step element containing hotspots
   * @param {string|null} activeTabId - Currently active tab ID
   */
  updateHotspotVisibility(stepElement, activeTabId) {
    const hotspotElements = stepElement.querySelectorAll(".hotspot");

    hotspotElements.forEach((hotspotElement) => {
      const hotspotId = hotspotElement.getAttribute("data-hotspot-id");
      const hotspotData = this.hotspots.get(hotspotId);

      if (!hotspotData) return;

      const hotspotConfig = hotspotData.config;
      const shouldShow =
        !activeTabId ||
        !hotspotConfig.visibleOnTab ||
        hotspotConfig.visibleOnTab === activeTabId;

      if (shouldShow) {
        hotspotElement.style.display = "flex";
        hotspotElement.style.opacity = "1";
        this.activeHotspots.add(hotspotId);
      } else {
        hotspotElement.style.display = "none";
        hotspotElement.style.opacity = "0";
        this.activeHotspots.delete(hotspotId);
      }
    });
  }

  /**
   * Add pulse animation to hotspots
   * @param {Array} hotspotIds - Array of hotspot IDs to animate
   */
  addPulseAnimation(hotspotIds) {
    hotspotIds.forEach((hotspotId) => {
      const hotspotData = this.hotspots.get(hotspotId);
      if (hotspotData) {
        hotspotData.element.classList.add("pulse");
      }
    });
  }

  /**
   * Remove pulse animation from hotspots
   * @param {Array} hotspotIds - Array of hotspot IDs to stop animating
   */
  removePulseAnimation(hotspotIds) {
    hotspotIds.forEach((hotspotId) => {
      const hotspotData = this.hotspots.get(hotspotId);
      if (hotspotData) {
        hotspotData.element.classList.remove("pulse");
      }
    });
  }

  /**
   * Show all hotspots in a step
   * @param {HTMLElement} stepElement - Step element
   */
  showAllHotspots(stepElement) {
    const hotspotElements = stepElement.querySelectorAll(".hotspot");
    hotspotElements.forEach((hotspot) => {
      hotspot.style.display = "flex";
      hotspot.style.opacity = "1";
    });
  }

  /**
   * Hide all hotspots in a step
   * @param {HTMLElement} stepElement - Step element
   */
  hideAllHotspots(stepElement) {
    const hotspotElements = stepElement.querySelectorAll(".hotspot");
    hotspotElements.forEach((hotspot) => {
      hotspot.style.display = "none";
      hotspot.style.opacity = "0";
    });
  }

  /**
   * Get hotspot statistics
   * @returns {Object} Statistics about hotspots
   */
  getHotspotStats() {
    return {
      totalHotspots: this.hotspots.size,
      activeHotspots: this.activeHotspots.size,
      hotspotIds: Array.from(this.hotspots.keys()),
      activeHotspotIds: Array.from(this.activeHotspots),
    };
  }

  /**
   * Reset hotspot manager state
   */
  reset() {
    this.hotspots.clear();
    this.activeHotspots.clear();
  }
}

// Export singleton instance
export const hotspotManager = new HotspotManager();
